/**
 * OK BOOMER CO -- Form System  v2.22
 * -----------------------------------
 * v2.22: Pre-position mascot at HEAD_TARGET immediately on script exec
 *        so there is no flash at translateY(0) before image load fires.
 *
 * v2.21: Fix mascot clamp minimum — HEAD_TARGET not 0, so the mascot
 *        never drifts above its start position when scrolling back up.
 *
 * v2.20: Mascot safety padding. Added FOOT_PAD=20 so the mascot never
 *        clips the container edge -- 20px buffer at head and foot.
 *        effTravel now spans (travel - HEAD_TARGET - FOOT_PAD) so the
 *        shift is clamped to [0, travel - FOOT_PAD].
 *
 * v2.19: LERP smoothing (0.05 per frame -- slow cinematic glide).
 *
 * v2.18: Definitive parallax -- fully dynamic effTravel.
 *        Eliminated TRAVEL_FACTOR hardcoded constant. effTravel is now
 *        computed from geometry so the mascot's feet reach the container
 *        bottom EXACTLY when the card bottom meets the viewport bottom --
 *        not at the page bottom. Snapshots initClamped at load so the
 *        head starts at HEAD_TARGET px from the container top.
 *
 *        FORM STATE  (travel >= 0):
 *          anchor            = initClamped  (snapshotted at page load)
 *          cardBottomClamped = containerH / (vh + containerH)
 *          effTravel         = (travel - HEAD_TARGET - FOOT_PAD) / (cardBottomClamped - anchor)
 *          shift             = HEAD_TARGET + (clamped - anchor) * effTravel
 *          -> at anchor:            shift = HEAD_TARGET        (head near top)
 *          -> at cardBottomClamped: shift = travel - FOOT_PAD  (feet 20px above bottom)
 *
 *        SUCCESS STATE  (travel < 0):  unchanged.
 *
 * v2.17: initClamped snapshot + TRAVEL_FACTOR=1.6.
 * v2.16: Dynamic naturalClamped anchor, removed static HEAD_OFFSET.
 */

class OKBForm {
  constructor(formEl) {
    this.form    = formEl;
    this.card    = formEl.closest('.okb-form-card') || formEl.parentElement;
    this.body    = this.card.querySelector('.okb-form__body');
    this.success = this.card.querySelector('.okb-form__success');
    this.btn     = formEl.querySelector('.okb-form__submit');

    this.fileZone     = formEl.querySelector('.okb-form__file-zone');
    this.fileInput    = formEl.querySelector('input[type="file"]');
    this.fileSelected = formEl.querySelector('.okb-form__file-selected');
    this.fileName     = formEl.querySelector('.okb-form__file-selected-name');
    this.fileWarning  = formEl.querySelector('.okb-form__file-warning');
    this.fileRemove   = formEl.querySelector('.okb-form__file-remove');
    this.submitError  = formEl.querySelector('.okb-form__submit-error');
    this.honeypot     = formEl.querySelector('.okb-honeypot');

    const gasUrl = (formEl.querySelector('.okb-gas-url') || {}).value || '';
    this.gasUrl = (gasUrl && !gasUrl.includes('PASTE_YOUR')) ? gasUrl : null;

    this.file = null;
    this.MAX_EMBED_BYTES = 22 * 1024 * 1024; // 22 MB

    this.form.setAttribute('novalidate', '');
    this._init();
  }

  _init() {
    this.form.addEventListener('submit', e => this._onSubmit(e), { capture: true });

    this.form.querySelectorAll('.okb-form__input, .okb-form__select, .okb-form__textarea')
      .forEach(inp => {
        inp.addEventListener('blur',  () => this._validateField(inp));
        inp.addEventListener('input', () => {
          if (inp.classList.contains('okb-error')) this._validateField(inp);
        });
      });

    if (this.fileZone) this._initFileZone();

    if (window.location.search.includes('contact%5B') ||
        window.location.search.includes('contact[')) {
      document.querySelectorAll('#ContactFooter-error').forEach(el => {
        el.style.display = 'none';
      });
    }
  }

  // -- FILE UPLOAD --

  _initFileZone() {
    this.fileZone.addEventListener('dragover', e => {
      e.preventDefault();
      this.fileZone.classList.add('okb-drag-over');
    });
    this.fileZone.addEventListener('dragleave', () => {
      this.fileZone.classList.remove('okb-drag-over');
    });
    this.fileZone.addEventListener('drop', e => {
      e.preventDefault();
      this.fileZone.classList.remove('okb-drag-over');
      const f = e.dataTransfer.files[0];
      if (f) this._setFile(f);
    });

    this.fileInput?.addEventListener('change', e => {
      if (e.target.files[0]) this._setFile(e.target.files[0]);
    });

    this.fileRemove?.addEventListener('click', e => {
      e.stopPropagation();
      this._clearFile();
    });
  }

  _setFile(f) {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

    if (!allowed.includes(f.type)) {
      this._showFileWarning('Unsupported file type. Please upload a JPG, PNG, or PDF.');
      return;
    }

    this.file = f;
    if (this.fileWarning)  this.fileWarning.classList.remove('okb-visible');
    if (this.fileName)     this.fileName.textContent = `${f.name}  (${this._fmtSize(f.size)})`;
    if (this.fileSelected) this.fileSelected.classList.add('okb-visible');

    const reqZone = this.form.querySelector('[data-require-file="true"]');
    if (reqZone) reqZone.classList.remove('okb-file-required-error');

    if (f.size > this.MAX_EMBED_BYTES) {
      this._showFileWarning(
        `File is ${this._fmtSize(f.size)} -- too large to attach. Please use a smaller file.`
      );
    }
  }

  _clearFile() {
    this.file = null;
    if (this.fileInput)    this.fileInput.value = '';
    if (this.fileSelected) this.fileSelected.classList.remove('okb-visible');
    if (this.fileWarning)  this.fileWarning.classList.remove('okb-visible');
  }

  _showFileWarning(msg) {
    if (!this.fileWarning) return;
    this.fileWarning.textContent = msg;
    this.fileWarning.classList.add('okb-visible');
  }

  _fmtSize(b) {
    if (b < 1024)        return b + ' B';
    if (b < 1024 * 1024) return (b / 1024).toFixed(0) + ' KB';
    return (b / (1024 * 1024)).toFixed(1) + ' MB';
  }

  // -- VALIDATION --

  _validateField(inp) {
    const field = inp.closest('.okb-form__field');
    const err   = field?.querySelector('.okb-form__field-error');
    let msg = '';

    if (inp.required && !inp.value.trim()) {
      msg = 'This field is required.';
    } else if (inp.type === 'email' && inp.value.trim() &&
               !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inp.value.trim())) {
      msg = 'Please enter a valid email address.';
    } else if (inp.tagName === 'SELECT' && inp.required && !inp.value) {
      msg = 'Please make a selection.';
    } else if (inp.validity && inp.validity.rangeOverflow) {
      msg = inp.dataset.errorMsg || 'This date is in the future.';
    } else if (inp.value.trim() && inp.validity && inp.validity.patternMismatch) {
      msg = inp.dataset.errorMsg || 'Please check the format of this field.';
    }

    inp.classList.toggle('okb-error', !!msg);
    if (err) {
      err.textContent = msg;
      err.classList.toggle('okb-visible', !!msg);
    }
    return !msg;
  }

  _validateAll() {
    let ok = true;

    this.form.querySelectorAll('.okb-form__input, .okb-form__select, .okb-form__textarea')
      .forEach(inp => {
        if (inp.offsetParent === null || inp.disabled) return;
        if (!this._validateField(inp)) ok = false;
      });

    this.form.querySelectorAll('input[type="checkbox"][required]').forEach(cb => {
      const valid = cb.checked;
      cb.classList.toggle('okb-error', !valid);
      if (!valid) ok = false;
    });

    const reqFileZone = this.form.querySelector('[data-require-file="true"]');
    if (reqFileZone && !this.file) {
      this._showFileWarning('Please upload your proof of purchase.');
      reqFileZone.classList.add('okb-file-required-error');
      ok = false;
    } else if (reqFileZone) {
      reqFileZone.classList.remove('okb-file-required-error');
    }

    return ok;
  }

  // -- SUBMISSION --

  _setLoading(on) {
    this.btn.disabled = on;
    this.btn.classList.toggle('okb-loading', on);
  }

  _showSuccess() {
    if (this.body)    this.body.style.display = 'none';
    if (this.success) this.success.classList.add('okb-visible');
    const top = this.card.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  _setSubmitError(msg) {
    if (!this.submitError) return;
    if (msg) {
      this.submitError.textContent = msg;
      this.submitError.classList.add('okb-visible');
    } else {
      this.submitError.classList.remove('okb-visible');
    }
  }

  _b64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async _onSubmit(e) {
    e.preventDefault();
    e.stopImmediatePropagation();

    this._setSubmitError(null);

    if (this.honeypot && this.honeypot.value) { this._showSuccess(); return; }

    if (!this._validateAll()) {
      this._setSubmitError('Please complete the required fields above.');
      const first = this.form.querySelector('.okb-error');
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    this._setLoading(true);

    try {
      if (this.gasUrl) {
        await this._submitViaGAS();
      } else {
        await this._submitViaShopify();
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      this._setSubmitError('Something went wrong -- please try again in a moment.');
    } finally {
      this._setLoading(false);
    }
  }

  async _submitViaGAS() {
    const n = fieldName => {
      const el = this.form.querySelector(`[name="contact[${fieldName}]"]:not([disabled])`);
      if (!el) return '';
      if (el.type === 'checkbox') return el.checked ? (el.value || 'Yes') : '';
      return el.value || '';
    };

    const message = (this.form.querySelector('[name="contact[body]"]') || {}).value || '';

    const formType = n('Form Type') || 'Contact Form';
    const isReg    = formType === 'Product Registration';

    const payload = {
      formType,
      firstName: n('First Name'),
      lastName:  n('Last Name'),
      email:     n('email'),
      phone:     n('Phone Number'),
      subject:   n('Subject') || (isReg ? 'Product Registration' : ''),
      message,
    };

    if (isReg) {
      payload.streetAddress      = n('Street Address');
      payload.addressLine2       = n('Address Line 2');
      payload.city               = n('City');
      payload.provinceState      = n('Province / State');
      payload.postalCode         = n('Postal Code');
      payload.country            = n('Country');
      payload.productModel       = n('Product Model');
      payload.serialNumber       = n('Serial Number');
      payload.purchaseDate       = n('Purchase Date');
      payload.purchasedFrom      = n('Purchased From');
      payload.purchasedFromOther = n('Purchased From (Other)');
      payload.termsAccepted      = n('Terms Accepted');
      payload.marketingOptIn     = n('Marketing Opt-In');
    }

    if (this.file) {
      try {
        const b64 = await this._b64(this.file);
        payload.attachment = { name: this.file.name, mimeType: this.file.type, data: b64 };
      } catch (_) {}
    }

    await fetch(this.gasUrl, {
      method:  'POST',
      mode:    'no-cors',
      headers: { 'Content-Type': 'text/plain' },
      body:    JSON.stringify(payload)
    });

    this._showSuccess();
  }

  async _submitViaShopify() {
    const fd = new FormData(this.form);

    if (this.file) {
      if (this.file.size <= this.MAX_EMBED_BYTES) {
        try {
          const b64 = await this._b64(this.file);
          fd.set('contact[Attachment]', `${this.file.name} (${this._fmtSize(this.file.size)})\n${b64}`);
        } catch (_) {}
      } else {
        fd.set('contact[Attachment Note]',
          `Customer selected: ${this.file.name} (${this._fmtSize(this.file.size)}) -- too large to embed.`);
      }
    }

    fd.delete('file_upload');

    const res = await fetch('/contact', {
      method:  'POST',
      body:    fd,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (res.redirected && res.url.includes('/challenge')) { window.location.href = res.url; return; }
    if (res.ok) { this._showSuccess(); } else { throw new Error('non-ok response'); }
  }
}

// -- AUTO-INIT --
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.okb-form').forEach(el => new OKBForm(el));

  // -- MASCOT SCROLL-REVEAL PARALLAX  v2.22 --
  //
  // Fully dynamic effTravel -- no hardcoded TRAVEL_FACTOR.
  //
  // FORM STATE (containerH >= imageH, travel >= 0):
  //   anchor            = initClamped  (snapshotted at page load / resize)
  //   cardBottomClamped = containerH / (vh + containerH)
  //   effTravel         = (travel - HEAD_TARGET - FOOT_PAD) / (cardBottomClamped - anchor)
  //   shift             = HEAD_TARGET + (clamped - anchor) * effTravel
  //   Hard-clamped to [HEAD_TARGET, travel - FOOT_PAD].
  //
  //   Guarantees:
  //     clamped == anchor            => shift == HEAD_TARGET        (head 25px from top)
  //     clamped == cardBottomClamped => shift == travel - FOOT_PAD  (feet 20px above bottom)
  //     scroll back up past init     => shift stays at HEAD_TARGET
  //     pre-positioned at HEAD_TARGET before image loads            (no flash at top)
  //
  // SUCCESS STATE (travel < 0): unchanged from v2.16.
  //
  (function initMascotParallax() {
    const imgCol = document.querySelector('.okb-contact-image-col');
    if (!imgCol) return;
    const img = imgCol.querySelector('img');
    if (!img) return;

    const HEAD_TARGET = 25;   // px from container top at page-load
    const FOOT_PAD    = 20;   // px safety buffer at container bottom
    const LERP        = 0.05; // smoothing factor per frame (5% of delta = slow cinematic glide)

    // Pre-position immediately so there's no flash at translateY(0) before init() fires
    if (window.innerWidth >= 750) {
      img.style.transform = 'translateX(-50%) translateY(' + HEAD_TARGET + 'px)';
    }

    let initClamped  = null;
    let targetShift  = 0;
    let currentShift = null; // null = snap on first frame
    let lerping      = false;
    let ticking      = false;

    function readClamped() {
      const rect       = imgCol.getBoundingClientRect();
      const vh         = window.innerHeight;
      const containerH = imgCol.offsetHeight;
      const raw        = (vh - rect.top) / (vh + containerH);
      return Math.max(0, Math.min(1, raw));
    }

    function computeTarget() {
      if (window.innerWidth < 750) {
        img.style.transform = 'translateX(-50%)';
        return;
      }

      const containerH = imgCol.offsetHeight;
      const imageH     = img.offsetHeight;
      const travel     = containerH - imageH;
      const clamped    = readClamped();
      const vh         = window.innerHeight;
      let shift;

      if (travel >= 0) {
        const anchor            = initClamped !== null ? initClamped : (vh / (vh + containerH));
        const cardBottomClamped = containerH / (vh + containerH);
        const range             = cardBottomClamped - anchor;
        const effTravel         = range > 0.001 ? (travel - HEAD_TARGET - FOOT_PAD) / range : (travel - FOOT_PAD) * 1.5;
        shift = HEAD_TARGET + (clamped - anchor) * effTravel;
        shift = Math.max(HEAD_TARGET, Math.min(travel - FOOT_PAD, shift));
      } else {
        const naturalClamped = vh / (vh + containerH);
        shift = -(naturalClamped * travel) + clamped * travel;
      }

      targetShift = shift;
      if (!lerping) { lerping = true; requestAnimationFrame(lerpStep); }
    }

    function lerpStep() {
      if (currentShift === null) currentShift = targetShift; // snap on first call
      const delta = targetShift - currentShift;
      if (Math.abs(delta) < 0.25) {
        currentShift = targetShift;
        lerping = false;
      } else {
        currentShift += delta * LERP;
        requestAnimationFrame(lerpStep);
      }
      img.style.transform = `translateX(-50%) translateY(${currentShift.toFixed(1)}px)`;
    }

    function init() {
      initClamped = readClamped();
      computeTarget();
      // Snap to initial position without lerp
      currentShift = targetShift;
      lerping = false;
      img.style.transform = `translateX(-50%) translateY(${currentShift.toFixed(1)}px)`;

      window.addEventListener('scroll', () => {
        if (!ticking) { requestAnimationFrame(() => { computeTarget(); ticking = false; }); ticking = true; }
      }, { passive: true });
      window.addEventListener('resize', () => {
        initClamped = readClamped();
        if (!ticking) { requestAnimationFrame(() => { computeTarget(); ticking = false; }); ticking = true; }
      }, { passive: true });
    }

    if (img.complete && img.naturalHeight > 0) {
      init();
    } else {
      img.addEventListener('load', init, { once: true });
    }
  })();
});
